"use client";

import { useState, useRef, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { cn } from "@/lib/utils";
import { DropdownMenu } from "@/components/tailwind/ui/dropdown-menu";
import type { DropdownMenuItemProps } from "@/components/tailwind/ui/dropdown-menu";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";

interface GroupsDropdownProps {
  memberEmail: string;
  memberId: string;
}

export default function GroupsDropdown({
  memberEmail,
  memberId,
}: GroupsDropdownProps) {
  const { currentWorkspace } = useWorkspaceContext();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get groups that contain this member
  const workspaceGroups = (currentWorkspace as any)?.groups || [];
  const memberGroups = workspaceGroups.filter((group: any) =>
    group.members?.some(
      (m: any) => m.userEmail === memberEmail || m.userId === memberId
    )
  );

  // Build menu items from groups (no icons, just titles)
  const menuItems: DropdownMenuItemProps[] = useMemo(() => {
    return memberGroups.map((group) => ({
      id: group._id?.toString() || group.id || group.name,
      label: group.name || "Unnamed Group",
      onClick: () => {
        // You can add click handler here if needed
        setIsOpen(false);
      },
    }));
  }, [memberGroups]);

  if (memberGroups.length === 0) {
    return (
      <div className="text-xs text-zinc-500 dark:text-zinc-400">No groups</div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="user-select-none transition-colors duration-200 ease-in cursor-pointer opacity-100 inline-flex items-center h-7 px-2 rounded-md whitespace-nowrap text-sm flex-shrink-0 leading-[1.2] min-w-0 text-zinc-900 dark:text-zinc-100 hover-component "
      >
        <span className="text-xs">
          {memberGroups.length} {memberGroups.length === 1 ? "group" : "groups"}
        </span>
        <ChevronDown
          className={cn(
            "w-2.5 h-full block fill-zinc-500 dark:fill-zinc-400 flex-shrink-0 ml-1 transition-transform",
            isOpen && "transform rotate-180"
          )}
        />
      </button>
      {isOpen && (
        <AnchoredPopover
          anchor={buttonRef}
          matchAnchorWidth
          constrainHeight={false}
          onClose={() => setIsOpen(false)}
          className="bg-white dark:bg-zinc-900 rounded-md shadow-lg border border-component min-w-[200px] max-w-[300px] max-h-[200px] overflow-y-auto"
        >
          <DropdownMenu items={menuItems} />
        </AnchoredPopover>
      )}
    </div>
  );
}
